import React, { useEffect, useRef, useState } from 'react';
import { Container, Header, SpaceBetween, Box, Spinner, Alert, Select } from '@amzn/awsui-components-react';
import { createEmbeddingContext } from 'amazon-quicksight-embedding-sdk';
import ApiCallService from 'src/services/ApiCallService';
import CONSTANTS from 'src/utils/constants';
import { ensureAuthenticated } from 'src/utils/auth/authenticate';

// Allowlist of users who can access Report Analysis — add/remove emails here
const ALLOWED_USERS = CONSTANTS.PAGE_NAV.REPORT_ANALYSIS.ALLOWED_USERS;

const TOPICS = [
    {
        label: 'TP Tax Margin Report (MAR / CWB / LEF)',
        value: 'SIO2mnosIdTyuPzB2HWtXMEGvZgSeLu2',
        description: 'P&L financials, PLI metrics (markup, Berry Ratio, ROS), and TP charges per CLI. Covers margin, combined workbook, and legal entity reports. Filter by provider code, workbook, or functional segment.',
    },
    {
        label: 'TP Tax Variance Report (VAR)',
        value: 'v5JdLUedJRekpgqkDaFVXsMP5XzXrxRq',
        description: 'Variance analysis — actual vs policy TP rates, variance amounts, root causes, report execution status, and approver details per CLI per period.',
    },
    {
        label: 'Apttus CLI Data',
        value: 'lQU6RBi0e8D8lnBQIiF60alywZEE6VXR',
        description: 'Master CLI metadata — agreement details, TP rates, tax owners, functional segments, workbook assignments, and CLI status. Use for lookups, missing data checks, and ownership queries.',
    },
];

export default function ReportAnalysis() {
    const containerRef = useRef<HTMLDivElement>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedTopic, setSelectedTopic] = useState<any>(null);
    const [userEmail, setUserEmail] = useState<string | null>(null);
    const [authorized, setAuthorized] = useState(false);

    useEffect(() => {
        const checkAccess = async () => {
            const auth = await ensureAuthenticated() as any;
            const session = auth.getSignInUserSession();
            const email = session.getIdToken().decodePayload().email;
            setUserEmail(email);
            setAuthorized(ALLOWED_USERS.includes(email));
        };
        checkAccess();
    }, []);

    useEffect(() => {
        if (!selectedTopic || !containerRef.current || !userEmail) return;
        let cancelled = false;

        const embedQuickSight = async () => {
            setLoading(true);
            setError(null);
            if (containerRef.current) containerRef.current.innerHTML = '';

            try {
                const response: any = await ApiCallService.getQuickSightEmbedUrl(
                    selectedTopic.value,
                    userEmail!
                );
                if (cancelled) return;

                const embedUrl = response.embedUrl;
                const context = await createEmbeddingContext();
                if (cancelled) return;

                await context.embedGenerativeQnA(
                    {
                        container: containerRef.current!,
                        height: '700px',
                        width: '100%',
                        url: embedUrl,
                    },
                    {
                        panelOptions: {
                            panelType: 'FULL',
                            title: selectedTopic.label,
                            showQIcon: true,
                        },
                    }
                );
                if (!cancelled) setLoading(false);
            } catch (err: any) {
                if (!cancelled) {
                    setError(err?.message || 'Failed to load QuickSight');
                    setLoading(false);
                }
            }
        };

        embedQuickSight();
        return () => { cancelled = true; };
    }, [selectedTopic, userEmail]);

    return (
        <SpaceBetween size="l">
            <Header variant="h1" description="Ask questions about TP Tax reports, variances, CLI metadata, and agreement details using natural language — powered by Amazon Q. For best results, ask about a specific CLI, workbook, or entity (e.g. 'Show details for CLI 00001797.0_03_R7-6F' or 'List all CLIs for provider code 2D').">
                IC Assist
            </Header>

            {!authorized ? (
                <Alert type="warning" header="Access Restricted">
                    You do not have access to Report Analysis. Contact the engineering team to request access.
                </Alert>
            ) : (
                <>
                    <Alert type="info" header="AI-Generated Responses">
                        Responses are generated by Amazon Q using AI and may not always be accurate or complete. Please verify critical data against source systems before making business decisions.
                    </Alert>
                    <Container header={<Header variant="h2">Select a dataset to analyze</Header>}>
                        <Select
                            selectedOption={selectedTopic}
                            onChange={({ detail }) => {
                                setSelectedTopic(detail.selectedOption);
                                setError(null);
                            }}
                            options={TOPICS.map(t => ({
                                label: t.label,
                                value: t.value,
                                description: t.description,
                            }))}
                            placeholder="Choose a dataset..."
                            filteringType="auto"
                        />
                    </Container>

                    {selectedTopic && (
                        <Container>
                            {loading && <Box textAlign="center" padding="xxl"><Spinner size="large" /> Loading QuickSight...</Box>}
                            {error && <Alert type="error" header="Error">{error}</Alert>}
                            <div ref={containerRef} style={{ height: '700px', width: '100%' }} />
                        </Container>
                    )}
                </>
            )}
        </SpaceBetween>
    );
}
